import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { useDashboardScreenshotStore } from "stores/dashboardScreenshot/useDashboardScreenshotStore";
import { selectNetworkDepthLegend, useMapStore } from "stores/map";
import { isDefined } from "utils/fp";

import { EntityKind } from "models/entityKind";
import { WidgetComponentType } from "models/screenshot";

import { ScreenshotSetting } from "components/screenshot/components";
import { WidgetBorderToggle } from "components/screenshot/components/WidgetBorderToggle";
import {
  SettingsControl,
  SettingsGroups,
  useScreenshotContext
} from "components/screenshot/hooks";
import { CHART_WIDGET_TYPES } from "components/shared/charts/hooks/chartWidgetTypes";
import { useUserSettings } from "components/user/hooks";

function useChartScreenshotSettingsControls(): SettingsControl[] {
  // hooks
  const { widget, settings } = useScreenshotContext();

  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );
  const networkDepthLegend = useMapStore(selectNetworkDepthLegend);

  const selectedShapefile = useSelector(
    (state: RootState) => state.project.selectedShapefile
  );

  const dashboardWidgets = useDashboardScreenshotStore((s) => s.activeWidgets);
  const dashboardHasMap = dashboardWidgets.some((w) => w.type === "map");
  const dashboardHasChart = dashboardWidgets.some((w) => CHART_WIDGET_TYPES.has(w.type));
  const userSettings = useUserSettings();
  const userBorderType = userSettings?.userDashboardSettings?.borderType;

  const MapSettingsGroups = {
    legend: "Upstream Legend",
    midstream_legend: "Midstream Legend",
    advanced: "Advanced Map Settings",
    ipdb_legend: "2D Geo Model Legend",
    network_depth_legend: "Network Depth Legend",
    shapefile_legend: "Shapefile & GeoTIFF Legend"
  };

  const controls: SettingsControl[] = [];

  const isMap = widget?.widgetComponentType === WidgetComponentType.Map;
  const isDashboardWithMap =
    widget?.widgetComponentType === WidgetComponentType.Dashboard && dashboardHasMap;

  if (!isMap && !isDashboardWithMap) {
    return controls;
  }

  // Only add legend size controls for individual map screenshots
  // or map-only dashboards (where chart controls don't provide these).
  const isMapOnlyDashboard = isDashboardWithMap && !dashboardHasChart;
  if (!isDashboardWithMap || isMapOnlyDashboard) {
    controls.push(
      {
        group: SettingsGroups.preset,
        control: (
          <ScreenshotSetting.Item
            key="legendTitleSize"
            name="legendTitleSize"
            label="Legend Title Size">
            <ScreenshotSetting.Input type="number" unit="pt" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: SettingsGroups.preset,
        control: (
          <ScreenshotSetting.Item
            key="legendFontSize"
            name="legendFontSize"
            label="Legend Label Size">
            <ScreenshotSetting.Input type="number" unit="pt" />
          </ScreenshotSetting.Item>
        )
      }
    );
  }

  // Show widget border toggle for map-only dashboards
  // (chart controls provide this for dashboards with charts).
  if (isMapOnlyDashboard && userBorderType !== "borderless") {
    controls.push({
      group: SettingsGroups.preset,
      control: <WidgetBorderToggle key="widgetBorderVisible" />
    });
  }

  controls.push(
    {
      group: SettingsGroups.quick,
      control: (
        <ScreenshotSetting.Item
          key="showLegendInScreenshot"
          name="showLegendInScreenshot"
          label="Upstream Legend"
          valuePropName="checked">
          <ScreenshotSetting.Input type="boolean" />
        </ScreenshotSetting.Item>
      )
    },
    {
      group: SettingsGroups.quick,
      control: (
        <ScreenshotSetting.Item
          key="showMidstreamLegendInScreenshot"
          name="showMidstreamLegendInScreenshot"
          label="Midstream Legend"
          valuePropName="checked">
          <ScreenshotSetting.Input type="boolean" />
        </ScreenshotSetting.Item>
      )
    },
    {
      group: SettingsGroups.quick,
      control: (
        <ScreenshotSetting.Item
          key="showIpdbLegendInScreenshot"
          name="showIpdbLegendInScreenshot"
          label="2D Geo Legend"
          valuePropName="checked">
          <ScreenshotSetting.Input type="boolean" />
        </ScreenshotSetting.Item>
      )
    }
  );

  if (
    activeEntityKinds.includes(EntityKind.Facility) &&
    networkDepthLegend?.legendItems?.length > 0
  ) {
    controls.push({
      group: SettingsGroups.quick,
      control: (
        <ScreenshotSetting.Item
          key="showNetworkDepthLegendInScreenshot"
          name="showNetworkDepthLegendInScreenshot"
          label="Network Legend"
          valuePropName="checked">
          <ScreenshotSetting.Input type="boolean" />
        </ScreenshotSetting.Item>
      )
    });
  }

  controls.push(
    {
      group: SettingsGroups.quick,
      control: (
        <ScreenshotSetting.Item
          key="showShapefileLegendInScreenshot"
          name="showShapefileLegendInScreenshot"
          label="Shapefile/GeoTIFF Legend"
          valuePropName="checked">
          <ScreenshotSetting.Input type="boolean" />
        </ScreenshotSetting.Item>
      )
    },
    ...(!isDashboardWithMap
      ? [
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="showTownshipBorderInScreenshot"
                name="showTownshipBorderInScreenshot"
                label="Township Border"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          },
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="applyAbbreviations"
                name="applyAbbreviations"
                label="Use Abbreviations"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          },
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="simplifyCustomFieldNames"
                name="simplifyCustomFieldNames"
                label="Simplify Custom Field Names"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          },
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="mapScreenshotScale"
                name="mapScreenshotScale"
                label="Scale">
                <ScreenshotSetting.Input type="number" min={0.1} step={0.1} />
              </ScreenshotSetting.Item>
            )
          }
        ]
      : [])
  );

  if (settings?.showLegendInScreenshot) {
    controls.push(
      {
        group: MapSettingsGroups.legend,
        control: (
          <ScreenshotSetting.Item
            key="mapLegendShowCounts"
            name="mapLegendShowCounts"
            label="Show Count"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: MapSettingsGroups.legend,
        control: (
          <ScreenshotSetting.Item
            key="showLegendBorderInScreenshot"
            name="showLegendBorderInScreenshot"
            label="Legend Border"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: MapSettingsGroups.legend,
        control: (
          <ScreenshotSetting.Item
            key="isWrappingTextUpstream"
            name="isWrappingTextUpstream"
            label="Text Wrap"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: MapSettingsGroups.legend,
        control: (
          <ScreenshotSetting.Item
            key="upstreamLegendOpacity"
            name="upstreamLegendOpacity"
            label="Opacity">
            <ScreenshotSetting.Input type="number" step={0.1} min={0} max={1} />
          </ScreenshotSetting.Item>
        )
      }
    );
  }

  if (settings?.showMidstreamLegendInScreenshot) {
    controls.push(
      {
        group: MapSettingsGroups.midstream_legend,
        control: (
          <ScreenshotSetting.Item
            key="midstreamLegendShowCounts"
            name="midstreamLegendShowCounts"
            label="Show Count"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: MapSettingsGroups.midstream_legend,
        control: (
          <ScreenshotSetting.Item
            key="showMidstreamLegendBorderInScreenshot"
            name="showMidstreamLegendBorderInScreenshot"
            label="Legend Border"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: MapSettingsGroups.midstream_legend,
        control: (
          <ScreenshotSetting.Item
            key="isWrappingTextMidstream"
            name="isWrappingTextMidstream"
            label="Text Wrap"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: MapSettingsGroups.midstream_legend,
        control: (
          <ScreenshotSetting.Item
            key="midstreamLegendOpacity"
            name="midstreamLegendOpacity"
            label="Opacity">
            <ScreenshotSetting.Input type="number" step={0.1} min={0} max={1} />
          </ScreenshotSetting.Item>
        )
      }
    );
  }

  if (settings?.showIpdbLegendInScreenshot) {
    controls.push(
      {
        group: MapSettingsGroups.ipdb_legend,
        control: (
          <ScreenshotSetting.Item
            key="showIpdbLegendBorderInScreenshot"
            name="showIpdbLegendBorderInScreenshot"
            label="Legend Border"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },

      ...(!isDashboardWithMap
        ? [
            {
              group: MapSettingsGroups.ipdb_legend,
              control: (
                <ScreenshotSetting.Item
                  key="ipdbTitleSize"
                  name="ipdbTitleSize"
                  label="Title Size">
                  <ScreenshotSetting.Input type="number" unit="pt" />
                </ScreenshotSetting.Item>
              )
            },
            {
              group: MapSettingsGroups.ipdb_legend,
              control: (
                <ScreenshotSetting.Item
                  key="ipdbLabelsSize"
                  name="ipdbLabelsSize"
                  label="Label Size">
                  <ScreenshotSetting.Input type="number" unit="pt" />
                </ScreenshotSetting.Item>
              )
            }
          ]
        : []),
      {
        group: MapSettingsGroups.ipdb_legend,
        control: (
          <ScreenshotSetting.Item
            key="ipdbLegendOpacity"
            name="ipdbLegendOpacity"
            label="Opacity">
            <ScreenshotSetting.Input type="number" step={0.1} min={0} max={1} />
          </ScreenshotSetting.Item>
        )
      }
    );
  }

  if (settings?.showNetworkDepthLegendInScreenshot) {
    controls.push(
      {
        group: MapSettingsGroups.network_depth_legend,
        control: (
          <ScreenshotSetting.Item
            key="showNetworkDepthLegendBorderInScreenshot"
            name="showNetworkDepthLegendBorderInScreenshot"
            label="Legend Border"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      ...(!isDashboardWithMap
        ? [
            {
              group: MapSettingsGroups.network_depth_legend,
              control: (
                <ScreenshotSetting.Item
                  key="networkDepthTitleSize"
                  name="networkDepthTitleSize"
                  label="Title Size">
                  <ScreenshotSetting.Input type="number" unit="pt" />
                </ScreenshotSetting.Item>
              )
            },
            {
              group: MapSettingsGroups.network_depth_legend,
              control: (
                <ScreenshotSetting.Item
                  key="networkDepthLabelsSize"
                  name="networkDepthLabelsSize"
                  label="Label Size">
                  <ScreenshotSetting.Input type="number" unit="pt" />
                </ScreenshotSetting.Item>
              )
            }
          ]
        : []),
      {
        group: MapSettingsGroups.network_depth_legend,
        control: (
          <ScreenshotSetting.Item
            key="networkDepthLegendOpacity"
            name="networkDepthLegendOpacity"
            label="Opacity">
            <ScreenshotSetting.Input type="number" step={0.1} min={0} max={1} />
          </ScreenshotSetting.Item>
        )
      }
    );
  }

  if (settings?.showShapefileLegendInScreenshot) {
    controls.push(
      ...(isDefined(selectedShapefile)
        ? [
            {
              group: MapSettingsGroups.shapefile_legend,
              control: (
                <ScreenshotSetting.Item
                  key="shapefileLegendShowDetails"
                  name="shapefileLegendShowDetails"
                  label="Show Details"
                  valuePropName="checked">
                  <ScreenshotSetting.Input type="boolean" />
                </ScreenshotSetting.Item>
              )
            }
          ]
        : []),
      {
        group: MapSettingsGroups.shapefile_legend,
        control: (
          <ScreenshotSetting.Item
            key="showShapefileLegendBorderInScreenshot"
            name="showShapefileLegendBorderInScreenshot"
            label="Legend Border"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      ...(!isDashboardWithMap
        ? [
            {
              group: MapSettingsGroups.shapefile_legend,
              control: (
                <ScreenshotSetting.Item
                  key="shapefileTitleSize"
                  name="shapefileTitleSize"
                  label="Title Size">
                  <ScreenshotSetting.Input type="number" unit="pt" />
                </ScreenshotSetting.Item>
              )
            },
            {
              group: MapSettingsGroups.shapefile_legend,
              control: (
                <ScreenshotSetting.Item
                  key="shapefileLabelsSize"
                  name="shapefileLabelsSize"
                  label="Label Size">
                  <ScreenshotSetting.Input type="number" unit="pt" />
                </ScreenshotSetting.Item>
              )
            }
          ]
        : []),
      {
        group: MapSettingsGroups.shapefile_legend,
        control: (
          <ScreenshotSetting.Item
            key="isWrappingTextShapefile"
            name="isWrappingTextShapefile"
            label="Text Wrap"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: MapSettingsGroups.shapefile_legend,
        control: (
          <ScreenshotSetting.Item
            key="shapefileLegendOpacity"
            name="shapefileLegendOpacity"
            label="Opacity">
            <ScreenshotSetting.Input type="number" step={0.1} min={0} max={1} />
          </ScreenshotSetting.Item>
        )
      }
    );
  }

  return controls;
}

export default useChartScreenshotSettingsControls;
